import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  ArrowLeft, Sun, Moon, User, LogOut, Search, ChevronRight,
  Loader2, Trophy, Users, Calendar, Globe, BookOpen, GraduationCap,
  CheckCircle, XCircle, Brain, Sparkles, Filter, Award, ExternalLink,
  Cpu, Stethoscope, Scale, Landmark, Shield, Building2, Briefcase,
  FlaskConical, Palette, BookOpenCheck, Leaf, X, Clock, Zap, FileText,
  ChevronLeft, AlertTriangle, Timer, Target, BarChart3, Flame, Sliders
} from 'lucide-react';
import { competitiveExamsApi, errorBookApi, topicProgressApi } from '../lib/api';
import { getExamPalette } from '../lib/examThemes';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { FontSizeSlider, useExamFontScale } from '../components/exam/FontSizeSlider';

const ICON_MAP = {
  cpu: Cpu, stethoscope: Stethoscope, scale: Scale, landmark: Landmark,
  shield: Shield, building: Building2, briefcase: Briefcase, flask: FlaskConical,
  'graduation-cap': GraduationCap, palette: Palette, 'book-open': BookOpenCheck, leaf: Leaf,
};

const DIFFICULTY_COLOR = {
  'Easy': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  'Moderate': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  'Hard': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  'Very Hard': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  'easy': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  'medium': 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  'hard': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export default function CompetitiveExams() {
  const [fontScale, setFontScale] = useExamFontScale();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [view, setView] = useState('catalog');
  const [categories, setCategories] = useState([]);
  const [exams, setExams] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [practiceLoading, setPracticeLoading] = useState(false);
  // Class-gate: competitive exams unlock at Class 10.  We surface a friendly
  // full-page message for lower classes instead of a generic error toast.
  const [gateBlocked, setGateBlocked] = useState(null);

  // Quick Answer Mode state
  const [quickQuestions, setQuickQuestions] = useState([]);
  const [quickIndex, setQuickIndex] = useState(0);
  const [quickAnswer, setQuickAnswer] = useState(null);
  const [quickChecked, setQuickChecked] = useState(false);
  const [quickScore, setQuickScore] = useState({ correct: 0, wrong: 0, total: 0 });

  // Real Exam Mode state
  const [realExam, setRealExam] = useState(null);
  const [realAnswers, setRealAnswers] = useState({});
  const [realSubmitted, setRealSubmitted] = useState(false);
  const [realTimeLeft, setRealTimeLeft] = useState(0);
  const [activeSection, setActiveSection] = useState(0);
  const timerRef = useRef(null);

  // Leaderboard state
  const [leaderboard, setLeaderboard] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [lbLoading, setLbLoading] = useState(false);
  const realStartRef = useRef(null);

  // Year selection (all competitive exams)
  const [selectedYear, setSelectedYear] = useState(''); // '' means "Not Required"
  const EXAM_YEARS = [2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015];

  useEffect(() => { loadData(); }, []);

  // Phase 40.11 — deep-link support: if we arrive with ?examId=..., auto-open
  // that exam's detail view.  This is what powers the back-button flow from
  // Mock Test / Exam Lab back into "the selected exam page".
  useEffect(() => {
    const deepExamId = searchParams.get('examId');
    if (deepExamId && exams.length > 0 && (!selectedExam || selectedExam.exam_id !== deepExamId)) {
      openDetail(deepExamId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exams, searchParams]);

  // Context-aware header back button.  Rules:
  //   • realExam / quickAnswer view → return to the selected exam's detail page.
  //   • detail view                 → return to the catalog.
  //   • catalog view                → go back to the dashboard.
  const handleHeaderBack = () => {
    if (view === 'realExam') {
      if (timerRef.current) clearInterval(timerRef.current);
      setView('detail');
      return;
    }
    if (view === 'quickAnswer') {
      setView('detail');
      return;
    }
    if (view === 'detail') {
      setSelectedExam(null);
      // Strip examId so a fresh Back click actually goes to dashboard.
      if (searchParams.get('examId')) {
        setSearchParams({}, { replace: true });
      }
      setView('catalog');
      return;
    }
    navigate('/dashboard');
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [cats, allExams] = await Promise.all([
        competitiveExamsApi.getCategories(),
        competitiveExamsApi.getAll()
      ]);
      setCategories(cats || []);
      setExams(allExams || []);
      setFilteredExams(allExams || []);
      setGateBlocked(null);
    } catch (err) {
      // Backend returns 403 with { code:'competitive_exams_class_gate', message }
      // when the student's class is below the class-10 unlock threshold —
      // surface a friendly explanation instead of a toast.
      if (err?.status === 403 && err?.code === 'competitive_exams_class_gate') {
        setGateBlocked({
          reason: err?.detail?.message || err.message || 'Competitive exams unlock at Class 10.',
          userClass: user?.class_level ?? user?.grade ?? null,
        });
      } else {
        toast.error('Failed to load exams');
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    let filtered = exams;
    if (selectedCategory) filtered = filtered.filter(e => e.category === selectedCategory);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(e =>
        e.name.toLowerCase().includes(q) ||
        e.full_name.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q)
      );
    }
    setFilteredExams(filtered);
  }, [selectedCategory, searchQuery, exams]);

  // Timer for Real Exam Mode
  useEffect(() => {
    if (view === 'realExam' && realTimeLeft > 0 && !realSubmitted) {
      timerRef.current = setInterval(() => {
        setRealTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            submitRealExam();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [view, realSubmitted]);

  const openDetail = async (examId) => {
    try {
      const detail = await competitiveExamsApi.getById(examId);
      setSelectedExam(detail);
      setSelectedYear('');
      setView('detail');
    } catch {
      toast.error('Failed to load exam details');
    }
  };

  // Quick Answer Mode
  const startQuickMode = async (examId) => {
    setPracticeLoading(true);
    try {
      const yearParam = selectedYear ? parseInt(selectedYear, 10) : null;
      const data = await competitiveExamsApi.generatePractice(examId, null, 10, yearParam);
      setQuickQuestions(data.questions || []);
      setQuickIndex(0);
      setQuickAnswer(null);
      setQuickChecked(false);
      setQuickScore({ correct: 0, wrong: 0, total: 0 });
      setView('quickAnswer');
    } catch {
      toast.error('Failed to generate questions');
    }
    setPracticeLoading(false);
  };

  const checkQuickAnswer = () => {
    if (quickAnswer === null) return;
    setQuickChecked(true);
    const q = quickQuestions[quickIndex];
    const isWrong = quickAnswer !== q.correct;
    setQuickScore(prev => ({
      correct: prev.correct + (isWrong ? 0 : 1),
      wrong: prev.wrong + (isWrong ? 1 : 0),
      total: prev.total + 1
    }));
    // Log wrong answers to error book (fire-and-forget)
    if (isWrong && selectedExam) {
      errorBookApi.log({
        exam_id: selectedExam.exam_id,
        subject: q.subject || selectedExam.subjects?.[0] || 'General',
        topic: q.topic || 'Unknown',
        year: q.year || (selectedYear ? parseInt(selectedYear, 10) : null),
        question: q.question,
        options: q.options || [],
        user_answer: q.options?.[quickAnswer] || String(quickAnswer),
        correct_answer: q.options?.[q.correct] || String(q.correct),
        explanation: q.explanation || '',
        source: 'quick',
      });
    }
    // Mark topic as practiced
    if (selectedExam && q.topic) {
      topicProgressApi.mark({
        exam_id: selectedExam.exam_id,
        subject: q.subject || selectedExam.subjects?.[0] || 'General',
        topic: q.topic,
        status: 'practiced',
      });
    }
  };

  const nextQuickQuestion = () => {
    if (quickIndex < quickQuestions.length - 1) {
      setQuickIndex(prev => prev + 1);
      setQuickAnswer(null);
      setQuickChecked(false);
    } else if (quickChecked) {
      // Last question checked - save score
      const pct = quickQuestions.length > 0 ? (quickScore.correct / quickQuestions.length) * 100 : 0;
      saveScore('quick', quickScore.correct, quickQuestions.length, pct);
    }
  };

  // Also save when last question is checked (isFinished)
  useEffect(() => {
    if (quickChecked && quickIndex === quickQuestions.length - 1 && quickQuestions.length > 0) {
      const finalCorrect = quickScore.correct;
      const pct = (finalCorrect / quickQuestions.length) * 100;
      saveScore('quick', finalCorrect, quickQuestions.length, pct);
    }
  }, [quickChecked, quickIndex, quickQuestions.length]);

  // Real Exam Mode
  const startRealMode = async (examId) => {
    setPracticeLoading(true);
    try {
      const data = await competitiveExamsApi.generateRealExam(examId);
      setRealExam(data);
      setRealAnswers({});
      setRealSubmitted(false);
      setActiveSection(0);
      setRealTimeLeft(data.time_minutes * 60);
      realStartRef.current = Date.now();
      setView('realExam');
    } catch {
      toast.error('Failed to generate exam paper');
    }
    setPracticeLoading(false);
  };

  const submitRealExam = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setRealSubmitted(true);
    toast.success('Exam submitted!');
  }, []);

  // Save real exam score when submitted
  useEffect(() => {
    if (realSubmitted && realExam) {
      const results = getRealExamResults();
      const pct = results.maxScore > 0 ? (results.score / results.maxScore) * 100 : 0;
      saveScore('real', results.correct, realExam.total_questions, pct);
      // Log every wrong answer to the error book
      realExam.sections.forEach((section, si) => {
        section.questions.forEach((q, qi) => {
          const key = `${si}-${qi}`;
          const userIdx = realAnswers[key];
          if (userIdx !== undefined && userIdx !== q.correct) {
            errorBookApi.log({
              exam_id: selectedExam?.exam_id,
              subject: section.name || 'General',
              topic: q.topic || section.name || 'Unknown',
              question: q.question,
              options: q.options || [],
              user_answer: q.options?.[userIdx] || String(userIdx),
              correct_answer: q.options?.[q.correct] || String(q.correct),
              explanation: q.explanation || '',
              source: 'real',
            });
          }
        });
      });
    }
  }, [realSubmitted]);

  const getRealExamResults = () => {
    if (!realExam) return { correct: 0, wrong: 0, unanswered: 0, score: 0, maxScore: 0 };
    let correct = 0, wrong = 0, unanswered = 0;
    realExam.sections.forEach((section, si) => {
      section.questions.forEach((q, qi) => {
        const key = `${si}-${qi}`;
        if (realAnswers[key] === undefined) unanswered++;
        else if (realAnswers[key] === q.correct) correct++;
        else wrong++;
      });
    });
    const score = correct * realExam.marks_correct + wrong * realExam.marks_wrong;
    const maxScore = realExam.total_questions * realExam.marks_correct;
    return { correct, wrong, unanswered, score, maxScore };
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return h > 0 ? `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}` : `${m}:${String(s).padStart(2,'0')}`;
  };

  const saveScore = async (mode, correct, total, percentage) => {
    if (!selectedExam) return;
    try {
      const timeTaken = realStartRef.current ? Math.round((Date.now() - realStartRef.current) / 1000) : 0;
      await competitiveExamsApi.submitScore(selectedExam.exam_id, {
        mode,
        score: correct,
        total,
        percentage: Math.round(percentage),
        correct,
        wrong: total - correct,
        time_taken_seconds: timeTaken
      });
    } catch {}
  };

  const loadLeaderboard = async (examId) => {
    setLbLoading(true);
    try {
      const data = await competitiveExamsApi.getLeaderboard(examId);
      setLeaderboard(data);
      setShowLeaderboard(true);
    } catch {
      toast.error('Failed to load leaderboard');
    }
    setLbLoading(false);
  };

  // ======================== CATALOG VIEW ========================
  const CatalogView = () => (
    <div className="space-y-6">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search exams (JEE, NEET, CLAT, UPSC...)"
          className="w-full pl-10 pr-4 py-3 rounded-xl border bg-background text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
          data-testid="exam-search" />
      </div>

      <div className="flex flex-wrap gap-2" data-testid="category-filters">
        <button onClick={() => setSelectedCategory(null)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!selectedCategory ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'}`}>
          All ({exams.length})
        </button>
        {categories.map(cat => {
          const Icon = ICON_MAP[cat.icon] || BookOpen;
          return (
            <button key={cat.id} onClick={() => setSelectedCategory(cat.id === selectedCategory ? null : cat.id)}
              data-testid={`cat-${cat.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                selectedCategory === cat.id ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
              }`}>
              <Icon className="w-3 h-3" />{cat.name} ({cat.count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : filteredExams.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No exams found. Try a different search.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="exams-grid">
          {filteredExams.map(exam => (
            <button key={exam.exam_id} onClick={() => openDetail(exam.exam_id)} data-testid={`exam-card-${exam.exam_id}`}
              className="p-5 rounded-xl border text-left transition-all hover:shadow-lg hover:border-primary/30 group bg-card">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-base group-hover:text-primary transition-colors">{exam.name}</h3>
                  <p className="text-xs text-muted-foreground">{exam.full_name}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DIFFICULTY_COLOR[exam.difficulty] || DIFFICULTY_COLOR['Moderate']}`}>
                  {exam.difficulty}
                </span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{exam.description}</p>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Users className="w-3 h-3" />{exam.applicants_yearly}</span>
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{exam.frequency}</span>
                <span className="flex items-center gap-1"><GraduationCap className="w-3 h-3" />Class {exam.target_grades?.join(', ')}</span>
                {exam.has_practice_questions && (
                  <span className="flex items-center gap-1 text-primary font-medium"><Brain className="w-3 h-3" />Practice</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ======================== DETAIL VIEW ========================
  const DetailView = () => {
    if (!selectedExam) return null;
    const e = selectedExam;
    const palette = getExamPalette(e);
    return (
      <div className="space-y-6" data-testid={`exam-detail-${palette.id}`}>
        <Button variant="ghost" onClick={() => setView('catalog')} className="mb-2" data-testid="back-to-catalog">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Catalog
        </Button>

        <div className={`relative overflow-hidden p-6 rounded-2xl border bg-gradient-to-br ${palette.heroBg}`}>
          <div className={`absolute -top-24 -right-24 w-64 h-64 rounded-full bg-gradient-to-br ${palette.heroFrom} ${palette.heroTo} opacity-10 blur-3xl pointer-events-none`} />
          <div className="flex items-start justify-between mb-4 relative">
            <div>
              <span className={`inline-block mb-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${palette.chip}`}>{palette.label}</span>
              <h2 className={`text-2xl font-bold bg-gradient-to-r ${palette.heroFrom} ${palette.heroTo} bg-clip-text text-transparent`}>{e.name}</h2>
              <p className="text-muted-foreground text-sm">{e.full_name}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${DIFFICULTY_COLOR[e.difficulty] || ''}`}>{e.difficulty}</span>
          </div>
          <p className="text-sm mb-4 relative">{e.description}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4 relative">
            <div className="p-3 rounded-lg bg-white/60 dark:bg-card/60 backdrop-blur-sm"><p className="text-xs text-muted-foreground">Conducted By</p><p className="text-sm font-medium">{e.conducting_body}</p></div>
            <div className="p-3 rounded-lg bg-white/60 dark:bg-card/60 backdrop-blur-sm"><p className="text-xs text-muted-foreground">Frequency</p><p className="text-sm font-medium">{e.frequency}</p></div>
            <div className="p-3 rounded-lg bg-white/60 dark:bg-card/60 backdrop-blur-sm"><p className="text-xs text-muted-foreground">Applicants</p><p className="text-sm font-medium">{e.applicants_yearly}</p></div>
            <div className="p-3 rounded-lg bg-white/60 dark:bg-card/60 backdrop-blur-sm"><p className="text-xs text-muted-foreground">Start Prep From</p><p className="text-sm font-medium">Class {e.preparation_start_grade}</p></div>
          </div>
        </div>

        <div className="p-5 rounded-xl border">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><GraduationCap className={`w-4 h-4 ${palette.accent}`} />Eligibility</h3>
          <p className="text-sm text-muted-foreground">{e.eligibility}</p>
        </div>

        <div className="p-5 rounded-xl border">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><BookOpen className={`w-4 h-4 ${palette.accent}`} />Exam Pattern</h3>
          <p className="text-sm text-muted-foreground">{e.exam_pattern}</p>
        </div>

        <div className="p-5 rounded-xl border">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><Award className={`w-4 h-4 ${palette.accent}`} />Subjects</h3>
          <div className="flex flex-wrap gap-2">
            {e.subjects?.map(s => (
              <span key={s} className={`px-3 py-1 rounded-full text-xs font-medium ${palette.chip}`}>{s}</span>
            ))}
          </div>
        </div>

        {e.top_colleges?.length > 0 && (
          <div className="p-5 rounded-xl border">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" />Top Colleges/Institutions</h3>
            <div className="flex flex-wrap gap-2">
              {e.top_colleges.map(c => (
                <span key={c} className="px-3 py-1 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 text-xs font-medium">{c}</span>
              ))}
            </div>
          </div>
        )}

        {e.important_websites?.length > 0 && (
          <div className="p-5 rounded-xl border">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Globe className="w-4 h-4 text-blue-500" />Official Websites</h3>
            <div className="flex flex-wrap gap-2">
              {e.important_websites.map(w => (
                <a key={w} href={`https://${w}`} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400 text-xs font-medium hover:underline">
                  {w}<ExternalLink className="w-3 h-3" />
                </a>
              ))}
            </div>
          </div>
        )}

        {e.sample_questions?.length > 0 && (
          <div className="p-5 rounded-xl border">
            <h3 className="font-semibold mb-3 flex items-center gap-2"><Brain className="w-4 h-4 text-violet-500" />Sample Questions</h3>
            <div className="space-y-3">
              {e.sample_questions.map((sq, qi) => (
                <div key={qi} className="p-3 rounded-lg bg-muted/50">
                  <p className="text-sm font-medium mb-2">{qi + 1}. {sq.question}</p>
                  <div className="grid grid-cols-2 gap-1.5 ml-4">
                    {sq.options?.map((opt, oi) => (
                      <span key={oi} className={`text-xs px-2 py-1 rounded ${oi === sq.correct ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-medium' : 'text-muted-foreground'}`}>
                        {String.fromCharCode(65 + oi)}. {opt}
                      </span>
                    ))}
                  </div>
                  {sq.subject && <span className="text-xs text-muted-foreground mt-1 inline-block">{sq.subject}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mode Selection Buttons */}
        <div className={`p-6 rounded-2xl border bg-gradient-to-br ${palette.heroBg} space-y-4`} data-testid="mode-selection">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Target className={`w-5 h-5 ${palette.accent}`} />Choose Your Practice Mode
          </h3>

          {/* Past Year Paper Selector (all competitive exams) */}
          <div className="p-4 rounded-xl border bg-white/70 dark:bg-card/70" data-testid="exam-year-selector">
            <label className="text-sm font-semibold flex items-center gap-2 mb-2">
              <Calendar className={`w-4 h-4 ${palette.accent}`} />Past Year Paper (Optional)
            </label>
            <p className="text-xs text-muted-foreground mb-3">
              Practice with questions styled after a specific year's {e.name} paper. Choose "Not Required" for a general mixed paper.
            </p>
            <select
              value={selectedYear}
              onChange={(ev) => setSelectedYear(ev.target.value)}
              data-testid="exam-year-dropdown"
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Not Required (Random Mix)</option>
              {EXAM_YEARS.map(y => (
                <option key={y} value={y}>{y} Paper Style</option>
              ))}
            </select>
            {selectedYear && (
              <Badge className={`mt-2 ${palette.chip}`} data-testid="exam-year-selected-badge">
                Generating {selectedYear} paper style
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => startQuickMode(e.exam_id)}
              disabled={practiceLoading}
              data-testid="quick-answer-mode-btn"
              className={`p-5 rounded-xl border-2 ${palette.modeBorder} bg-white dark:bg-card text-left transition-all hover:shadow-lg hover:scale-[1.02] group`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${palette.iconBg}`}>
                  <Zap className={`w-5 h-5 ${palette.iconFg}`} />
                </div>
                <div>
                  <h4 className={`font-bold text-sm transition-colors group-hover:${palette.accent}`}>Quick Answer Mode</h4>
                  <p className="text-[10px] text-muted-foreground">Instant feedback</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Answer one question at a time. Check your answer immediately and see detailed explanations before moving to the next question.</p>
            </button>

            <button
              onClick={() => startRealMode(e.exam_id)}
              disabled={practiceLoading}
              data-testid="real-exam-mode-btn"
              className={`p-5 rounded-xl border-2 ${palette.modeBorder} bg-white dark:bg-card text-left transition-all hover:shadow-lg hover:scale-[1.02] group`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${palette.iconBg}`}>
                  <FileText className={`w-5 h-5 ${palette.iconFg}`} />
                </div>
                <div>
                  <h4 className={`font-bold text-sm transition-colors group-hover:${palette.accent}`}>Real Exam Mode</h4>
                  <p className="text-[10px] text-muted-foreground">Full exam simulation</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Timed exam following the actual pattern with sections, negative marking, and results at the end. Just like the real exam.</p>
            </button>

            <button
              onClick={() => navigate(`/mock-test?examId=${e.exam_id}${selectedYear ? `&year=${selectedYear}` : ''}`)}
              data-testid="nta-mock-mode-btn"
              className={`p-5 rounded-xl border-2 ${palette.modeBorder} bg-white dark:bg-card text-left transition-all hover:shadow-lg hover:scale-[1.02] group`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${palette.iconBg}`}>
                  <Flame className={`w-5 h-5 ${palette.iconFg}`} />
                </div>
                <div>
                  <h4 className={`font-bold text-sm transition-colors group-hover:${palette.accent}`}>NTA Mock Test</h4>
                  <p className="text-[10px] text-muted-foreground">Full sectional timed mock</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">Proper NTA-pattern paper with sectional timer, +4/-1 marking, question palette and mark-for-review. Submit → Errors auto-added to your Error Book.</p>
            </button>

            <button
              onClick={() => navigate(`/exam-lab?examId=${e.exam_id}`)}
              data-testid="exam-lab-btn"
              className={`p-5 rounded-xl border-2 ${palette.modeBorder} bg-white dark:bg-card text-left transition-all hover:shadow-lg hover:scale-[1.02] group`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${palette.iconBg}`}>
                  <Sliders className={`w-5 h-5 ${palette.iconFg}`} />
                </div>
                <div>
                  <h4 className={`font-bold text-sm transition-colors group-hover:${palette.accent}`}>Exam Lab</h4>
                  <p className="text-[10px] text-muted-foreground">Weightage + custom builder</p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">See which topics appear most in past papers (frequency heat-map) and build a custom test from any chapters, difficulty, and year style.</p>
            </button>
          </div>
          {practiceLoading && (
            <div className="flex items-center justify-center gap-2 py-3">
              <Loader2 className={`w-5 h-5 animate-spin ${palette.accent}`} />
              <span className="text-sm text-muted-foreground">Generating exam paper...</span>
            </div>
          )}
          <Button variant="outline" size="sm" className="w-full" onClick={() => loadLeaderboard(e.exam_id)} disabled={lbLoading} data-testid="detail-leaderboard-btn">
            <Trophy className="w-4 h-4 mr-2 text-amber-500" />{lbLoading ? 'Loading...' : 'View Leaderboard'}
          </Button>
        </div>
      </div>
    );
  };

  // ======================== QUICK ANSWER MODE VIEW ========================
  const QuickAnswerView = () => {
    if (!quickQuestions.length) return null;
    const q = quickQuestions[quickIndex];
    const isLast = quickIndex === quickQuestions.length - 1;
    const isFinished = quickChecked && isLast;
    const palette = getExamPalette(selectedExam);

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setView('detail')} data-testid="back-from-quick">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to {selectedExam?.name}
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-xs px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-medium flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />{quickScore.correct}
            </span>
            <span className="text-xs px-3 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-medium flex items-center gap-1">
              <XCircle className="w-3 h-3" />{quickScore.wrong}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div className={`relative overflow-hidden p-4 rounded-xl border bg-gradient-to-br ${palette.heroBg}`}>
          <div className={`absolute -top-16 -right-16 w-48 h-48 rounded-full bg-gradient-to-br ${palette.heroFrom} ${palette.heroTo} opacity-10 blur-2xl pointer-events-none`} />
          <div className="flex items-center justify-between mb-2 relative">
            <span className="text-sm font-semibold flex items-center gap-2">
              <Zap className={`w-4 h-4 ${palette.accent}`} />Quick Answer Mode — {selectedExam?.name}
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${palette.chip}`}>{palette.label}</span>
            </span>
            <span className="text-xs text-muted-foreground">Question {quickIndex + 1} of {quickQuestions.length}</span>
          </div>
          <Progress value={((quickIndex + (quickChecked ? 1 : 0)) / quickQuestions.length) * 100} className="h-2 relative" />
        </div>

        {/* Question Card */}
        <div className="p-6 rounded-2xl border bg-card" data-testid={`quick-question-${quickIndex}`}>
          <div className="flex items-start gap-3 mb-5">
            <span className="shrink-0 w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">{quickIndex + 1}</span>
            <div className="flex-1">
              <p className="font-medium text-base leading-relaxed">{q.question}</p>
              {q.difficulty && <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${DIFFICULTY_COLOR[q.difficulty] || 'bg-muted'}`}>{q.difficulty}</span>}
            </div>
          </div>

          <div className="space-y-3 ml-12">
            {q.options?.map((opt, oi) => {
              const isSelected = quickAnswer === oi;
              const isCorrect = oi === q.correct;
              let style = 'border-border hover:border-primary/40 cursor-pointer';
              if (quickChecked) {
                if (isCorrect) style = 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 ring-1 ring-emerald-400';
                else if (isSelected) style = 'border-red-400 bg-red-50 dark:bg-red-950/30';
                else style = 'border-border opacity-60';
              } else if (isSelected) {
                style = 'border-primary bg-primary/5 ring-1 ring-primary';
              }

              return (
                <div key={oi}
                  className={`flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all ${style}`}
                  onClick={() => !quickChecked && setQuickAnswer(oi)}
                  data-testid={`quick-option-${oi}`}>
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0 ${
                    quickChecked && isCorrect ? 'bg-emerald-500 text-white border-emerald-500' :
                    quickChecked && isSelected ? 'bg-red-500 text-white border-red-500' :
                    isSelected ? 'bg-primary text-primary-foreground border-primary' : 'border-muted-foreground/30'
                  }`}>
                    {String.fromCharCode(65 + oi)}
                  </span>
                  <span className="flex-1 text-sm">{opt}</span>
                  {quickChecked && isCorrect && <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />}
                  {quickChecked && isSelected && !isCorrect && <XCircle className="w-5 h-5 text-red-500 shrink-0" />}
                </div>
              );
            })}
          </div>

          {/* Explanation */}
          {quickChecked && q.explanation && (
            <div className="mt-5 ml-12 p-4 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800" data-testid="quick-explanation">
              <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 mb-1 flex items-center gap-1">
                <Brain className="w-3 h-3" />Explanation
              </p>
              <p className="text-sm text-blue-800 dark:text-blue-300">{q.explanation}</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          {!quickChecked ? (
            <Button onClick={checkQuickAnswer} disabled={quickAnswer === null}
              className="flex-1 h-12 text-base font-semibold bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
              data-testid="check-answer-btn">
              <CheckCircle className="w-5 h-5 mr-2" />Check Answer
            </Button>
          ) : isFinished ? (
            <div className="flex-1 space-y-3">
              <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-indigo-500/10 border">
                <p className="font-bold text-center text-lg" data-testid="quick-final-score">
                  Final Score: {quickScore.correct}/{quickQuestions.length} ({Math.round(quickScore.correct / quickQuestions.length * 100)}%)
                </p>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => startQuickMode(selectedExam?.exam_id)} className="flex-1" data-testid="retry-quick-btn">
                  <Sparkles className="w-4 h-4 mr-2" />New Questions
                </Button>
                <Button variant="outline" onClick={() => loadLeaderboard(selectedExam?.exam_id)} disabled={lbLoading} data-testid="view-leaderboard-btn">
                  <Trophy className="w-4 h-4 mr-2" />{lbLoading ? 'Loading...' : 'Leaderboard'}
                </Button>
                <Button variant="outline" onClick={() => setView('detail')}>Back to Exam</Button>
              </div>
            </div>
          ) : (
            <Button onClick={nextQuickQuestion}
              className="flex-1 h-12 text-base font-semibold bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
              data-testid="next-question-btn">
              Next Question <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </div>
    );
  };

  // ======================== REAL EXAM MODE VIEW ========================
  const RealExamView = () => {
    if (!realExam) return null;
    const section = realExam.sections[activeSection];
    const results = realSubmitted ? getRealExamResults() : null;
    const palette = getExamPalette(selectedExam);

    return (
      <div className="space-y-4">
        {/* Exam Header Bar */}
        <div className={`sticky top-[57px] z-40 -mx-4 px-4 py-3 bg-gradient-to-r ${palette.heroBg} backdrop-blur border-b`} data-testid="real-exam-header">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {!realSubmitted && (
                <Button variant="ghost" size="sm" onClick={() => {
                  if (window.confirm('Are you sure you want to leave? Your progress will be lost.')) {
                    if (timerRef.current) clearInterval(timerRef.current);
                    setView('detail');
                  }
                }} data-testid="exit-real-exam">
                  <X className="w-4 h-4" />
                </Button>
              )}
              <div>
                <h3 className={`text-sm font-bold bg-gradient-to-r ${palette.heroFrom} ${palette.heroTo} bg-clip-text text-transparent`}>{realExam.exam_name} — Real Exam</h3>
                <p className="text-[10px] text-muted-foreground">{realExam.exam_pattern}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!realSubmitted && (
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-sm font-bold ${
                  realTimeLeft < 300 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 animate-pulse' : 'bg-muted'
                }`} data-testid="exam-timer">
                  <Clock className="w-4 h-4" />
                  {formatTime(realTimeLeft)}
                </div>
              )}
              <span className="text-xs text-muted-foreground">
                {Object.keys(realAnswers).length}/{realExam.total_questions} answered
              </span>
            </div>
          </div>

          {/* Section Tabs */}
          <div className="flex gap-2 mt-2 overflow-x-auto pb-1" data-testid="section-tabs">
            {realExam.sections.map((s, si) => {
              const answeredInSection = s.questions.filter((_, qi) => realAnswers[`${si}-${qi}`] !== undefined).length;
              return (
                <button key={si} onClick={() => setActiveSection(si)}
                  data-testid={`section-tab-${si}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    activeSection === si ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
                  }`}>
                  {s.name}
                  <span className="ml-1.5 opacity-70">({answeredInSection}/{s.questions.length})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Results Panel (after submission) */}
        {realSubmitted && results && (
          <div className="p-6 rounded-2xl border bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/20 dark:to-indigo-950/20" data-testid="exam-results">
            <h3 className="font-bold text-xl mb-4 flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-violet-500" />Exam Results
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <div className="p-3 rounded-xl bg-white dark:bg-card border">
                <p className="text-xs text-muted-foreground">Score</p>
                <p className="text-2xl font-bold text-primary">{results.score}/{results.maxScore}</p>
              </div>
              <div className="p-3 rounded-xl bg-white dark:bg-card border">
                <p className="text-xs text-muted-foreground">Percentage</p>
                <p className="text-2xl font-bold text-primary">{Math.round(results.score / results.maxScore * 100)}%</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800">
                <p className="text-xs text-emerald-600">Correct</p>
                <p className="text-2xl font-bold text-emerald-600">{results.correct}</p>
              </div>
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                <p className="text-xs text-red-600">Wrong</p>
                <p className="text-2xl font-bold text-red-600">{results.wrong}</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                <p className="text-xs text-amber-600">Unanswered</p>
                <p className="text-2xl font-bold text-amber-600">{results.unanswered}</p>
              </div>
            </div>
            {realExam.negative_marking && (
              <p className="text-xs text-muted-foreground">Marking: +{realExam.marks_correct} for correct, {realExam.marks_wrong} for wrong, 0 for unanswered</p>
            )}
            <div className="flex gap-3 mt-4">
              <Button onClick={() => startRealMode(selectedExam?.exam_id)} data-testid="retake-exam-btn">
                <Sparkles className="w-4 h-4 mr-2" />Retake Exam
              </Button>
              <Button variant="outline" onClick={() => loadLeaderboard(selectedExam?.exam_id)} disabled={lbLoading} data-testid="real-leaderboard-btn">
                <Trophy className="w-4 h-4 mr-2" />{lbLoading ? 'Loading...' : 'Leaderboard'}
              </Button>
              <Button variant="outline" onClick={() => setView('detail')}>Back to Exam Details</Button>
            </div>
          </div>
        )}

        {/* Questions */}
        <div className="space-y-4" data-testid="exam-questions" style={{ zoom: fontScale }}>
          <div className="flex justify-end">
            <FontSizeSlider scale={fontScale} onChange={setFontScale} />
          </div>
          {section?.questions.map((q, qi) => {
            const key = `${activeSection}-${qi}`;
            const answered = realAnswers[key];
            return (
              <div key={qi} className={`p-5 rounded-xl border ${
                realSubmitted
                  ? answered === q.correct
                    ? 'border-emerald-300 bg-emerald-50/30 dark:border-emerald-800 dark:bg-emerald-950/20'
                    : answered !== undefined
                      ? 'border-red-300 bg-red-50/30 dark:border-red-800 dark:bg-red-950/20'
                      : 'border-amber-300 bg-amber-50/30 dark:border-amber-800 dark:bg-amber-950/20'
                  : 'border-border'
              }`} data-testid={`real-q-${activeSection}-${qi}`}>
                <div className="flex items-start gap-3 mb-3">
                  <span className="shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold">{qi + 1}</span>
                  <div className="flex-1">
                    <p className="font-medium text-sm">{q.question}</p>
                    {q.difficulty && <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${DIFFICULTY_COLOR[q.difficulty] || 'bg-muted'}`}>{q.difficulty}</span>}
                  </div>
                  {realSubmitted && answered === q.correct && <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />}
                  {realSubmitted && answered !== undefined && answered !== q.correct && <XCircle className="w-5 h-5 text-red-500 shrink-0" />}
                  {realSubmitted && answered === undefined && <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />}
                </div>
                <div className="space-y-2 ml-10">
                  {q.options?.map((opt, oi) => {
                    let optStyle = 'border-border hover:border-primary/40 cursor-pointer';
                    if (realSubmitted) {
                      if (oi === q.correct) optStyle = 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30';
                      else if (answered === oi) optStyle = 'border-red-400 bg-red-50 dark:bg-red-950/30';
                      else optStyle = 'border-border opacity-60';
                    } else if (answered === oi) {
                      optStyle = 'border-primary bg-primary/5';
                    }
                    return (
                      <div key={oi}
                        className={`flex items-center space-x-3 p-2.5 rounded-lg border transition-all ${optStyle}`}
                        onClick={() => !realSubmitted && setRealAnswers(prev => ({ ...prev, [key]: oi }))}
                        data-testid={`real-option-${activeSection}-${qi}-${oi}`}>
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border shrink-0 ${
                          (realSubmitted && oi === q.correct) ? 'bg-emerald-500 text-white border-emerald-500' :
                          (realSubmitted && answered === oi) ? 'bg-red-500 text-white border-red-500' :
                          answered === oi ? 'bg-primary text-primary-foreground border-primary' : 'border-muted-foreground/30'
                        }`}>{String.fromCharCode(65 + oi)}</span>
                        <span className="flex-1 text-sm">{opt}</span>
                      </div>
                    );
                  })}
                </div>
                {realSubmitted && q.explanation && (
                  <p className="text-xs text-muted-foreground mt-3 ml-10 p-2 rounded bg-muted/50">{q.explanation}</p>
                )}
              </div>
            );
          })}
        </div>

        {/* Submit Button */}
        {!realSubmitted && (
          <Button onClick={() => {
            if (window.confirm(`Submit exam? You have answered ${Object.keys(realAnswers).length} of ${realExam.total_questions} questions.`)) {
              submitRealExam();
            }
          }}
            className="w-full h-12 text-base font-semibold bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
            data-testid="submit-real-exam-btn">
            <FileText className="w-5 h-5 mr-2" />Submit Exam
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleHeaderBack} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <Trophy className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold">Competitive Exams</h1>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium">
                {exams.length}+ Exams
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>{theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full"><User className="w-4 h-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/profile')}><User className="w-4 h-4 mr-2" />Profile</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}><LogOut className="w-4 h-4 mr-2" />Logout</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {gateBlocked ? (
          <div className="max-w-xl mx-auto text-center py-14 space-y-5" data-testid="competitive-gate-blocked">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-lg">
              <Sparkles className="w-9 h-9" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Competitive exams unlock at Class 10</h2>
              {gateBlocked.userClass && (
                <p className="text-sm text-muted-foreground mt-1">
                  You&apos;re currently in <span className="font-semibold text-foreground">Class {gateBlocked.userClass}</span> — a little more to go!
                </p>
              )}
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              JEE, NEET, CLAT, GATE and the other national-level exams are built for Class 10 students and above.
              For now, dive into your school syllabus and interactive labs — you&apos;ll be ready when the time comes.
            </p>
            <div className="flex flex-wrap gap-3 justify-center pt-2">
              <Button onClick={() => navigate('/board-exams')} data-testid="gate-goto-boards">
                <FileText className="w-4 h-4 mr-2" />Board Exams
              </Button>
              <Button variant="outline" onClick={() => navigate('/learning-hub')} data-testid="gate-goto-hub">
                <BookOpen className="w-4 h-4 mr-2" />Learning Hub
              </Button>
              <Button variant="outline" onClick={() => navigate('/labs')} data-testid="gate-goto-labs">
                <Zap className="w-4 h-4 mr-2" />Visual Labs
              </Button>
            </div>
          </div>
        ) : (
          <>
            {view === 'catalog' && <CatalogView />}
            {view === 'detail' && <DetailView />}
            {view === 'quickAnswer' && <QuickAnswerView />}
            {view === 'realExam' && <RealExamView />}
          </>
        )}
      </main>

      {/* Leaderboard Modal */}
      {showLeaderboard && leaderboard && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowLeaderboard(false)}>
          <div className="w-full max-w-lg bg-card rounded-2xl border shadow-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="leaderboard-modal">
            <div className="p-5 border-b flex items-center justify-between bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold">Leaderboard</h3>
                  <p className="text-xs text-muted-foreground">{selectedExam?.name} - Top Performers</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setShowLeaderboard(false)}><X className="w-4 h-4" /></Button>
            </div>
            <div className="overflow-y-auto max-h-[60vh] p-4 space-y-2">
              {leaderboard.leaderboard?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Trophy className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No scores yet. Be the first!</p>
                </div>
              ) : (
                leaderboard.leaderboard?.map((entry) => (
                  <div key={entry.user_id}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${entry.is_me ? 'bg-primary/5 border-primary/30 ring-1 ring-primary/20' : 'bg-card'}`}
                    data-testid={`lb-entry-${entry.rank}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      entry.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                      entry.rank === 2 ? 'bg-slate-300 text-slate-700' :
                      entry.rank === 3 ? 'bg-amber-700 text-white' :
                      'bg-muted text-muted-foreground'
                    }`}>{entry.rank}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{entry.user_name} {entry.is_me && <Badge className="text-[9px] ml-1">You</Badge>}</div>
                      <p className="text-[10px] text-muted-foreground">{entry.attempts} attempt{entry.attempts > 1 ? 's' : ''}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className={`text-sm font-bold ${
                        entry.best_percentage >= 80 ? 'text-emerald-500' : entry.best_percentage >= 50 ? 'text-amber-500' : 'text-red-500'
                      }`}>{entry.best_percentage}%</p>
                      <p className="text-[10px] text-muted-foreground">{entry.best_score}/{entry.best_total}</p>
                    </div>
                  </div>
                ))
              )}
              {/* My rank if not in top 50 */}
              {leaderboard.my_rank && !leaderboard.leaderboard?.some(e => e.is_me) && (
                <div className="border-t pt-3 mt-3">
                  <p className="text-[10px] text-muted-foreground mb-1">Your Position</p>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-primary/5 border border-primary/30">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-bold">{leaderboard.my_rank.rank}</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{leaderboard.my_rank.user_name} <Badge className="text-[9px] ml-1">You</Badge></div>
                    </div>
                    <span className="text-sm font-bold text-primary">{leaderboard.my_rank.best_percentage}%</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
